import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import BackToMarketing from './BackToMarketing';
import { clientHasDemoAgent } from '../utils/onboardingGate';
import { auth } from '../firebase/config';

const TEMPLATES = [
  { id: 'receptionist', label: 'AI Assistant (calls)', emoji: '📞' },
  { id: 'lead_qual', label: 'Lead Qualification Agent', emoji: '🎯' },
  { id: 'appointment', label: 'Appointment Booking Agent', emoji: '📅' },
  { id: 'support', label: 'Customer Support Agent', emoji: '💬' },
  { id: 'service_business', label: 'Service Business Assistant', emoji: '🔧' },
  { id: 'custom', label: 'Custom Business Assistant', emoji: '✨' }
];

const VOICE_OPTIONS = [
  { value: 'alloy', label: 'Alloy (Neutral)' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'nova', label: 'Nova' },
  { value: 'shimmer', label: 'Shimmer' }
];

export default function OnboardingFlow({ onComplete }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({
    templateId: 'receptionist',
    businessName: '',
    agentName: '',
    businessDescription: '',
    servicesOffered: '',
    businessHours: '',
    contactEmail: '',
    notes: '',
    voice: 'alloy'
  });

  const totalSteps = 4;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uid = auth.currentUser?.uid;
        if (uid && (await clientHasDemoAgent(uid))) {
          if (!cancelled) navigate('/dashboard', { replace: true });
          return;
        }
        const me = await api('/api/onboarding/me');
        if (cancelled) return;
        if (me.demo_assistant_id || me.onboardingStage === 'demo_ready' || me.onboardingStage === 'build_requested') {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch {
        /* API 404 / offline — stay on onboarding or already redirected via Firestore */
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const next = () => currentStep < totalSteps && setCurrentStep((s) => s + 1);
  const prev = () => currentStep > 1 && setCurrentStep((s) => s - 1);

  const canNext = () => {
    if (currentStep === 1) return !!form.templateId;
    if (currentStep === 2)
      return (
        form.businessName.trim() &&
        form.agentName.trim() &&
        form.businessDescription.trim()
      );
    if (currentStep === 3) return !!form.voice;
    return true;
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const res = await api('/api/onboarding/demo-assistant', {
        method: 'POST',
        body: JSON.stringify({
          templateId: form.templateId,
          businessName: form.businessName,
          agentName: form.agentName,
          businessDescription: form.businessDescription,
          servicesOffered: form.servicesOffered,
          businessHours: form.businessHours,
          contactEmail: form.contactEmail,
          notes: form.notes,
          voice: form.voice
        })
      });
      const agent = res.agent;
      localStorage.setItem('agentData', JSON.stringify(agent));
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...user, agent }));
      onComplete?.(agent);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      console.error(e);
      alert(e.message || 'Could not create demo agent. Is the API server running with VAPI_API_KEY?');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center px-4">
        <div className="w-10 h-10 border-2 border-purple-500/40 border-t-purple-400 rounded-full animate-spin" />
        <p className="mt-4 text-sm font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>Loading…</p>
        <p className="mt-1 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Setting up your experience</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative overflow-hidden flex items-center justify-center px-4 py-6">
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
        <BackToMarketing className="px-3 py-2 rounded-lg bg-black/30 backdrop-blur-sm border border-white/10 hover:bg-white/10" />
      </div>

      <div className="relative z-10 w-full max-w-3xl">
        {/* Appy mascot header (visual only) */}
        <div className="flex justify-center mb-3">
          <img
            src="/screenshots/appymascot.png.png"
            alt="Appy - The Appify AI Mascot"
            className="w-14 h-14 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden w-14 h-14 rounded-full bg-white/10 text-white items-center justify-center font-bold text-sm">
            Appy
          </div>
        </div>
        <div className="mb-4">
          <div className="flex justify-between text-white text-sm mb-2">
            <span>Step {currentStep} of {totalSteps}</span>
            <span>{Math.round((currentStep / totalSteps) * 100)}%</span>
          </div>
          <div className="h-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <div
              className="h-1.5 rounded-full transition-all"
              style={{ width: `${(currentStep / totalSteps) * 100}%`, background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            />
          </div>
        </div>

        <div className="rounded-2xl p-4 md:p-6" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Choose a template</h2>
              <p className="text-gray-300 mb-6">Starts your demo agent with the right tone.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => update('templateId', t.id)}
                    className="p-3 rounded-xl text-left text-sm transition"
                    style={
                      form.templateId === t.id
                        ? { background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.5)', color: '#fff' }
                        : { backgroundColor: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.65)' }
                    }
                  >
                    <span className="text-xl mr-2">{t.emoji}</span>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-3 text-left">
              <h2 className="text-2xl font-bold text-white">Business setup</h2>
              {[
                ['businessName', 'Business name', 'text', true],
                ['agentName', 'Agent name (how they introduce themselves)', 'text', true],
                ['businessDescription', 'Business description', 'textarea', true],
                ['servicesOffered', 'Services offered', 'textarea', false],
                ['businessHours', 'Business hours', 'text', false],
                ['contactEmail', 'Contact email', 'email', false],
                ['notes', 'Notes (optional)', 'textarea', false]
              ].map(([key, label, type, req]) =>
                type === 'textarea' ? (
                  <div key={key}>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</label>
                    <textarea
                      value={form[key]}
                      onChange={(e) => update(key, e.target.value)}
                      rows={key === 'businessDescription' ? 4 : 2}
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                      placeholder={req ? 'Required' : 'Optional'}
                    />
                  </div>
                ) : (
                  <div key={key}>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</label>
                    <input
                      type={type}
                      value={form[key]}
                      onChange={(e) => update(key, e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                    />
                  </div>
                )
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Voice</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {VOICE_OPTIONS.map((v) => (
                  <button
                    key={v.value}
                    type="button"
                    onClick={() => update('voice', v.value)}
                    className="p-3 rounded-xl text-left text-sm transition"
                    style={
                      form.voice === v.value
                        ? { background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.5)', color: '#fff' }
                        : { backgroundColor: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.65)' }
                    }
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="text-center text-white">
              <h2 className="text-2xl font-bold mb-4">Create your demo agent</h2>
              <p className="text-gray-300 mb-6">
                We’ll create a preview voice agent in Vapi (no phone number). You can test it from your dashboard.
              </p>
              <ul className="text-left text-gray-300 text-sm space-y-1 max-w-md mx-auto mb-6">
                <li>Template: {TEMPLATES.find((t) => t.id === form.templateId)?.label}</li>
                <li>Business: {form.businessName}</li>
                <li>Agent: {form.agentName}</li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-3 mt-6">
          <button
            type="button"
            onClick={prev}
            disabled={currentStep === 1}
            className="px-4 sm:px-6 py-3 rounded-xl text-sm text-white transition disabled:opacity-40" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
          >
            Back
          </button>
          {currentStep < totalSteps ? (
            <button
              type="button"
              disabled={!canNext()}
              onClick={next}
              className="px-6 sm:px-8 py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={handleCreate}
              className="px-6 sm:px-8 py-3 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
            >
              {loading ? 'Creating…' : 'Create demo agent'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
